import { useEffect, useState } from 'react';

// Browser default root font-size. The app's whole type scale is rem-based, so
// the live root font-size IS the text-scale multiplier (1 = 16px = 100%).
const BASE_PX = 16;

function readScale(): number {
  if (typeof window === 'undefined') return 1;
  const px = parseFloat(getComputedStyle(document.documentElement).fontSize);
  return Number.isFinite(px) && px > 0 ? px / BASE_PX : 1;
}

/**
 * Current root font-size scale (1 = 16px / 100%). Tracks both the OS/browser
 * text-size setting and the dev text-scale tool (which sets an inline
 * font-size on <html>).
 *
 * Why a hook and not a CSS media query: media-query `em`/`rem` resolve against
 * the browser's *default* font-size, not the `<html>` override the app uses to
 * scale text — so CSS literally can't branch on the current text scale. Use
 * this when a layout needs a hard cutoff at a text size (e.g. swap to
 * abbreviated labels above a threshold).
 */
export function useRootFontScale(): number {
  const [scale, setScale] = useState(readScale);

  useEffect(() => {
    const update = () => setScale(readScale());
    window.addEventListener('resize', update);
    // The dev tool (and OS-driven changes routed through it) set font-size via
    // an inline style on <html>; observe it so the scale updates without a
    // resize event.
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style'],
    });
    return () => {
      window.removeEventListener('resize', update);
      observer.disconnect();
    };
  }, []);

  return scale;
}
